import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import type { Inquiry } from '../api/types';
import { ErrorMessage } from '../components/ErrorMessage';
import { PageLoader } from '../components/Loader';
import { MutationError } from '../components/MutationError';
import { useDebounced } from '../components/useDebounced';
import { STATUS_LABELS } from '../expose/model';
import { AppointmentForm } from '../ops/AppointmentForm';
import { fmtDateTime } from '../ops/format';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { matchedByLabels, sourceLabel } from './labels';

// InquiryPanel: Immobilienanfrage an der Rückfrage – was erkannt wurde, Klärung ohne Objekt, Besichtigung vereinbaren.
export function InquiryPanel({ ticketId }: { ticketId: string }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const list = useQuery({ queryKey: projectKeys.inquiries(project.id, { ticket_id: ticketId }), queryFn: ({ signal }) => papi.inquiries({ ticket_id: ticketId }, signal) });
  if (list.isPending) return <PageLoader label="Anfrage wird geladen …" />;
  if (list.isError) return <ErrorMessage error={list.error} />;
  if (list.data.items.length === 0) return null;
  return (
    <section className="card" aria-labelledby="inquiry-title">
      <h2 id="inquiry-title">{list.data.items.length > 1 ? 'Anfragen' : 'Anfrage'}</h2>
      {list.data.items.map((q) => (
        <InquiryCard key={q.id} inquiry={q} ticketId={ticketId} />
      ))}
    </section>
  );
}

export function InquiryCard({ inquiry: q, ticketId }: { inquiry: Inquiry; ticketId?: string }) {
  const { project, can } = useProject();
  const [planning, setPlanning] = useState(false);
  const queryClient = useQueryClient();
  const canView = q.status === 'MATCHED' && !!q.contact_id && !!q.lead_id && can('appointment.create');
  return (
    <article className="inquiry-card" aria-label={`Anfrage von ${q.prospect_name || q.prospect_email || 'Interessent'}`}>
      <dl className="facts">
        <dt>Quelle</dt>
        <dd>
          {sourceLabel(q)}
          {q.portal_object_id && <span className="muted"> · Objekt-ID {q.portal_object_id}</span>}
          {q.requested_at && <span className="muted"> · {fmtDateTime(q.requested_at)}</span>}
        </dd>
        <dt>Objekt</dt>
        <dd>
          {q.object_number ?? q.object_ref ?? 'nicht erkannt'}
          {q.matched_by && <span className="muted"> · {matchedByLabels[q.matched_by] ?? q.matched_by}</span>}
        </dd>
        <dt>Interessent</dt>
        <dd>
          {q.prospect_name || '–'}
          {[q.prospect_email, q.prospect_phone].filter(Boolean).length > 0 && (
            <span className="muted block">{[q.prospect_email, q.prospect_phone].filter(Boolean).join(' · ')}</span>
          )}
        </dd>
        {q.desired_appointment && (
          <>
            <dt>Wunschtermin</dt>
            <dd>{q.desired_appointment}</dd>
          </>
        )}
        {q.message && (
          <>
            <dt>Nachricht</dt>
            <dd className="pre-wrap">{q.message}</dd>
          </>
        )}
        {q.viewings.length > 0 && (
          <>
            <dt>Besichtigungen</dt>
            <dd>
              <ul className="plain-list">
                {q.viewings.map((v) => (
                  <li key={v.appointment_id}>
                    <Link to={`/p/${project.key}/termine/${v.appointment_id}`}>{fmtDateTime(v.start_at)}</Link>
                    {v.status === 'CANCELED' && <span className="muted"> · abgesagt</span>}
                  </li>
                ))}
              </ul>
            </dd>
          </>
        )}
      </dl>
      {q.status === 'UNMATCHED' && can('ticket.manage') && <AssignObject inquiry={q} ticketId={ticketId} />}
      {canView && !planning && (
        <button type="button" className="btn btn-primary" onClick={() => setPlanning(true)}>
          Besichtigung vereinbaren
        </button>
      )}
      {canView && planning && q.lead_id && q.contact_id && (
        <ViewingForm
          inquiry={q}
          onDone={() => {
            setPlanning(false);
            void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
          }}
        />
      )}
    </article>
  );
}

// ViewingForm: Terminmaske für die Besichtigung – Treffpunkt ist die Anschrift des Objekts (Lead des Eigentümers).
function ViewingForm({ inquiry: q, onDone }: { inquiry: Inquiry; onDone: () => void }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const leadId = q.lead_id ?? '';
  const lead = useQuery({ queryKey: ['projects', project.id, 'lead', leadId], queryFn: ({ signal }) => papi.lead(leadId, signal) });
  if (lead.isPending) return <PageLoader label="Objekt wird geladen …" />;
  const l = lead.data;
  return (
    <AppointmentForm
      leadId={leadId}
      viewing={{ contactId: q.contact_id ?? '', exposeId: q.expose_id, name: q.prospect_name || q.prospect_email || 'Interessent', email: q.prospect_email, phone: q.prospect_phone }}
      defaults={{ street: l?.street, houseNumber: l?.house_number, postalCode: l?.postal_code, city: l?.city }}
      onSaved={onDone}
      onCancel={onDone}
    />
  );
}

function AssignObject({ inquiry: q, ticketId }: { inquiry: Inquiry; ticketId?: string }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState(q.object_ref ?? '');
  const [exposeId, setExposeId] = useState('');
  const term = useDebounced(search, 300);
  const objects = useQuery({ queryKey: ['projects', project.id, 'inquiry-objects', term], queryFn: ({ signal }) => papi.inquiryObjects(term, signal) });
  const assign = useMutation({
    mutationFn: () => papi.assignInquiry(q.id, exposeId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
      if (ticketId) void queryClient.invalidateQueries({ queryKey: projectKeys.ticket(project.id, ticketId) });
    },
  });
  return (
    <div className="inquiry-assign">
      <p className="muted">Kein eindeutiges Objekt erkannt. Nach der Zuordnung entstehen Interessent und Aufgabe; die Portal-Objekt-ID wird für künftige Anfragen gelernt.</p>
      <label className="field wide">
        <span>Objekt suchen (Objektnummer, Straße, PLZ, Ort)</span>
        <input value={search} onChange={(e) => setSearch(e.target.value)} />
      </label>
      {objects.data && objects.data.items.length === 0 && <p className="muted">Kein Exposé gefunden.</p>}
      {objects.data && objects.data.items.length > 0 && (
        <ul className="plain-list candidate-list" aria-label="Exposés">
          {objects.data.items.map((o) => (
            <li key={o.expose_id}>
              <label className="checkbox">
                <input type="radio" name={`inquiry-object-${q.id}`} checked={exposeId === o.expose_id} onChange={() => setExposeId(o.expose_id)} />{' '}
                <strong>{o.object_number}</strong> <span className="muted">{[o.address, STATUS_LABELS[o.status] ?? o.status].filter(Boolean).join(' · ')}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="btn btn-primary" disabled={!exposeId || assign.isPending} onClick={() => assign.mutate()}>
        Objekt zuordnen
      </button>
      {assign.isError && <MutationError error={assign.error} />}
    </div>
  );
}
